import type { TooltipItem } from "chart.js";

import { ProjectIssuanceConstants } from "../../constants/project-issuance";
import { ColorConstants } from "../../constants/styles";
import { DataGridColumnCurrency, InventorySurface, ProjectIssuanceBNG, SelectData } from "../../models";
import { ResultData } from "../../service/Shared";
import { ChartDatasets, DataGridColumnDefinition, StackedChartData } from "../../widget";
import { formatNumber } from "../common";
import { convertDateToStringDDMMYYYY, convertDateToStringYYYYMMDD, parseDateOnlyStringYYYYMMDD } from "../date";
import { flattenObject } from "../flattenObject";
import { jsonReviver } from "../json";

interface InventoryIssuanceLike {
  standardSpecificIssuanceData: string;
  vintageStartDate: Date | null;
  vintageEndDate: Date | null;
  [key: string]: unknown;
}

const {
  ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY,
  ISSUANCE_UNIT_MEASUREMENT_CARBON,
  ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY,
  ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY,
  ISSUANCE_UNIT_MEASUREMENT_RESTORATION,
} = ProjectIssuanceConstants;

/**
 * The unit a measurement's quantities are expressed in, or undefined where they are a bare count.
 *
 * Only Nutrient Neutrality has one: its quantities are an annual RATE, so a figure without "kg/yr"
 * beside it is ambiguous. Returning undefined for everything else is what keeps the four consuming
 * surfaces byte-identical for Carbon, Biodiversity and Ecosystem recovery.
 *
 * Lowercase "kg/yr" per D-4, deliberately differing from the Figma chart component's "Kg/yr".
 */
export const getInventoryUnitLabel = (unitMeasurement: string): string | undefined =>
  unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY
    ? ProjectIssuanceConstants.ISSUANCE_NUTRIENT_UNIT_LABEL
    : undefined;

/**
 * The unit label as the two chart surfaces append it: a leading space, so a y-axis tick or a tooltip
 * can concatenate it straight onto the formatted value.
 *
 * The space belongs to that formatting rather than to the label, which is why `getInventoryUnitLabel`
 * stays a bare unit and this wrapper carries the presentation.
 */
export const getInventoryUnitSuffix = (unitMeasurement: string): string | undefined => {
  const unitLabel = getInventoryUnitLabel(unitMeasurement);
  return unitLabel ? ` ${unitLabel}` : undefined;
};

/**
 * The stacked-bar tooltip both aggregate inventory surfaces use: the series, the formatted value
 * with whatever prefix and suffix the view carries, and the bar's own label in brackets.
 *
 * Shared here rather than exported from either hook - the landholding page previously reached across
 * routes into the inventory management board's hook to import it.
 */
export const inventoryTooltipLabel = (context: TooltipItem<"bar">, prefix?: string, suffix?: string): string => {
  const projectName = context.chart.data.labels?.[context.dataIndex];
  return `${context.dataset.label}: ${prefix || ""}${formatNumber(context.parsed.y)}${suffix || ""} (${projectName})`;
};

export const getInventoryColumns = (
  unitMeasurement: string,
  showPrice: boolean,
  showAvailable: boolean
): DataGridColumnDefinition[] => {
  /*
    Nutrient Neutrality quantities are an annual rate, so every quantity column carries "kg/yr".
    Empty for every other measurement, which leaves their headers byte-identical.
  */
  const unitLabel = getInventoryUnitLabel(unitMeasurement);
  const quantitySuffix = unitLabel ? ` (${unitLabel})` : "";

  let columns: DataGridColumnDefinition[] = [
    {
      name: "Serial Number",
      key: "serialNumber",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: false,
    },
    {
      name: `Total${quantitySuffix}`,
      key: "unitQuantityTotal",
      dataType: "number",
      filterable: true,
      sortable: false,
    },
  ];

  if (showAvailable) {
    columns = [
      ...columns,
      {
        name: `Available${quantitySuffix}`,
        key: "unitQuantityAvailable",
        dataType: "number",
        filterable: true,
        sortable: false,
      },
    ];
  }

  switch (unitMeasurement) {
    case ISSUANCE_UNIT_MEASUREMENT_CARBON:
    case ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY:
      columns.splice(0, 0, {
        name: "Vintage",
        key: "vintage",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: false,
        sortable: false,
      });
      columns.splice(5, 0, {
        name: "Unit type",
        key: "quantityType",
        dataType: "string",
        filterable: true,
        sortable: false,
      });
      break;
    case ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY:
      columns.splice(
        0,
        0,
        {
          name: "Habitat group",
          key: "habitatType",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        },
        {
          name: "Habitat",
          key: "habitat",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        },
        {
          name: "Distinctiveness",
          key: "distinctiveness",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        }
      );
      columns.splice(3, 0, {
        name: "Issuance date",
        key: "issuanceDate",
        dataType: "Date",
        formatter: "dateOnly",
        filterable: false,
        sortable: false,
      });
      break;
    /*
      Nutrient Neutrality (GAP-3 - this function is the NN grid for both consuming surfaces, the
      pre-access tab and the public page). No vintage, because NN issuances carry none; no habitat,
      area or unit-length column, all Biodiversity concepts; no NCA, which the NN issuance schema
      does not declare.

      `quantityType` leads and is named "Nutrient" rather than "Unit type", matching the
      authenticated project grid. It must stay first and outside totalColumnNames: the totals row
      labels itself by writing "Total" into the first visible column it does not sum.

      Quoted sits with the availability columns and is gated with them - a project not listed as
      available for sale exposes neither.

      LPA and LNRS are deliberately absent. The SDD has them "captured but hidden by default, behind
      Table options", and neither consuming grid renders an Edit columns panel (`visible` on a column
      definition is declared but never read), so there would be nowhere to un-hide them from. They
      belong to whichever ticket gives these two surfaces that affordance.
    */
    case ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY:
      columns.splice(
        0,
        0,
        {
          name: "Nutrient",
          key: "quantityType",
          dataType: "string",
          filterable: true,
          sortable: true,
          minWidth: 120,
        },
        {
          name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.eligibility,
          key: "eligibility",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        },
        {
          name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.yearsEligible,
          key: "yearsEligible",
          dataType: "number",
          filterable: true,
          sortable: true,
          minWidth: 160,
        },
        {
          name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.designatedSite,
          key: "designatedSite",
          dataType: "string",
          formatter: "stringWithEllipsis",
          filterable: true,
          sortable: true,
        }
      );

      if (showAvailable) {
        columns = [
          ...columns,
          {
            name: `Quoted${quantitySuffix}`,
            key: "quotedUnits",
            dataType: "number",
            filterable: true,
            sortable: true,
            minWidth: 140,
          },
        ];
      }
      break;
    /*
      Restoration (Restore 30x30, KAN-9934). One issuance per project with a single unit type, no
      vintage, verification status or habitat - so no vintage, habitat, area, Sold or Reserved
      columns. Total / Available / Price are the shared ones, gated as for every measurement.

      `quantityType` leads, as on the NN grid, and must stay first and outside totalColumnNames:
      the totals row labels itself by writing "Total" into the first visible column it does not sum.
    */
    case ISSUANCE_UNIT_MEASUREMENT_RESTORATION:
      columns.splice(
        0,
        0,
        {
          name: "Unit type",
          key: "quantityType",
          dataType: "string",
          filterable: true,
          sortable: true,
        },
        {
          name: "Issuance date",
          key: "issuanceDate",
          dataType: "Date",
          formatter: "dateOnly",
          filterable: false,
          sortable: false,
        }
      );
      break;
    default:
      break;
  }

  if (showPrice) {
    columns = [
      ...columns,
      {
        name: "Price",
        key: "price",
        dataType: "number",
        formatter: "currency",
        currency: DataGridColumnCurrency.Gbp,
        filterable: true,
        sortable: false,
      },
    ];
  }

  return columns;
};

export const getVintageLabel = (issuance: InventoryIssuanceLike): string => {
  return `${issuance.vintageStartDate?.getFullYear()} - ${issuance.vintageEndDate?.getFullYear()}`;
};

export const getHabitatFromStandardSpecificIssuanceData = (standardSpecificIssuanceData: string): string[] => {
  const biodiversityData = JSON.parse(standardSpecificIssuanceData, jsonReviver) as ProjectIssuanceBNG;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return [biodiversityData.habitatType!, biodiversityData.habitatDistinctiveness!];
};

export const mapInventoryIssuancesToRows = (
  issuances: InventoryIssuanceLike[],
  unitMeasurement: string
): ResultData[] => {
  return (
    issuances?.map((issuance) => {
      const row = flattenObject(issuance);

      if (
        unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_CARBON ||
        unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY
      ) {
        row.vintage = getVintageLabel(issuance);
      }

      return row;
    }) ?? []
  );
};

/**
 * How an inventory surface splits verified units from pending ones.
 *
 * The project surface tests the quantity type while the public and asset-manager surfaces test the
 * verification status, so the same project can show a different verified/pending split depending on
 * where it is opened. That disagreement is REAL and preserved; naming it here makes it explicit
 * rather than accidental, so a future ticket can resolve it deliberately.
 */
export type InventoryVerifiedBy = "quantityType" | "verificationStatus" | "none";

/** Quantity types the project inventory counts as verified. Omits HICO and EIU - see KAN-9740. */
export const PROJECT_VERIFIED_QUANTITY_TYPES = [
  ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_WCU,
  ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_PCU,
  ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_WCC,
];

export const INVENTORY_VERIFIED_STATUS = "Verified";

/**
 * Returns the predicate a surface uses to decide whether an issuance is verified. `none` is for the
 * grouped Biodiversity branches, which have no pending concept at all.
 */
export const getInventoryVerifiedPredicate =
  (verifiedBy: InventoryVerifiedBy) =>
  (issuance: { quantityType?: string | null; verificationStatus?: string | null }): boolean => {
    switch (verifiedBy) {
      case "quantityType":
        return PROJECT_VERIFIED_QUANTITY_TYPES.includes(issuance.quantityType ?? "");
      case "verificationStatus":
        return issuance.verificationStatus === INVENTORY_VERIFIED_STATUS;
      case "none":
      default:
        return true;
    }
  };

export interface InventoryChartShape {
  /** How the x axis is grouped. */
  groupBy: "vintage" | "habitat" | "quantityType";
  /** Which field decides the verified/pending split, or "none" where there is no split. */
  verifiedBy: InventoryVerifiedBy;
  /** Whether the vintage x-axis label carries spaces around the hyphen. */
  spacedVintageLabel: boolean;
}

/**
 * The per-surface chart shape. This is the table from the ticket's section 4 expressed once, so the
 * four surfaces' differences are readable side by side instead of buried in four separate switches.
 *
 * Note what is deliberately NOT uniform: the verified/pending field differs between project and the
 * other two, and the x-axis label format differs between the asset-manager surface (spaced, via
 * getVintageLabel) and the project and public ones (unspaced, inlined). Both are preserved.
 */
export const getInventoryChartShape = (surface: InventorySurface, unitMeasurement: string): InventoryChartShape => {
  const isBiodiversity = unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY;

  /*
    Nutrient Neutrality draws one bar per nutrient and has no verified/pending concept at all, so it
    populates only the available/reserved/sold series. `verifiedBy: "none"` states that as a typed
    fact rather than leaving it to a fallthrough.

    Without this the asset-manager and public surfaces fell to their vintage branch, and NN issuances
    have no vintage - every bar took the label "undefined - undefined" (unspaced on public) and the
    whole inventory collapsed onto one column.

    The dashboard is excluded because it does not build its chart through this resolver at all.
  */
  if (surface !== "dashboard" && unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY) {
    return { groupBy: "quantityType", verifiedBy: "none", spacedVintageLabel: surface === "assetManagerPreAccess" };
  }

  /*
    Restoration (Restore 30x30, KAN-9934) takes the same shape as Nutrient Neutrality: its issuance
    carries no vintage, so the vintage branch labelled every bar "undefined-undefined", and it has no
    verified/pending concept, so every issuance counts as verified - matching the key, which draws no
    Pending block for it. It groups on the quantity type, the single "30x30 Unit".
  */
  if (surface !== "dashboard" && unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_RESTORATION) {
    return { groupBy: "quantityType", verifiedBy: "none", spacedVintageLabel: surface === "assetManagerPreAccess" };
  }

  switch (surface) {
    case "project":
      return {
        groupBy: isBiodiversity ? "habitat" : "vintage",
        verifiedBy: isBiodiversity ? "none" : "quantityType",
        spacedVintageLabel: false,
      };
    case "public":
      return {
        groupBy: isBiodiversity ? "habitat" : "vintage",
        verifiedBy: isBiodiversity ? "none" : "verificationStatus",
        spacedVintageLabel: false,
      };
    case "assetManagerPreAccess":
    default:
      return {
        groupBy: isBiodiversity ? "habitat" : "vintage",
        verifiedBy: isBiodiversity ? "none" : "verificationStatus",
        spacedVintageLabel: true,
      };
  }
};

/** The vintage x-axis label in the format the given surface uses. */
export const getInventoryXAxisLabel = (surface: InventorySurface, issuance: InventoryIssuanceLike): string =>
  getInventoryChartShape(surface, ISSUANCE_UNIT_MEASUREMENT_CARBON).spacedVintageLabel
    ? getVintageLabel(issuance)
    : `${issuance.vintageStartDate?.getFullYear()}-${issuance.vintageEndDate?.getFullYear()}`;

export interface InventoryDefaultSort {
  /** The DataGrid column key. "" where the surface does not seed the grid's own sort. */
  gridColumnKey: string;
  /** The key sent to the server. */
  serverSortKey: string;
}

/**
 * The default sort for a surface, returning the grid key and the server key TOGETHER.
 *
 * Returning both is the point: the asset-manager surface previously returned the SERVER key where a
 * grid column key belonged, so its Biodiversity default sort silently matched no column and did
 * nothing. Fixed on KAN-9741 as a called-out change (register entry C-9).
 *
 * The project surface seeds no grid sort of its own, only the request, hence the empty grid key.
 */
export const getInventoryDefaultSort = (surface: InventorySurface, unitMeasurement: string): InventoryDefaultSort => {
  const isBiodiversity = unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY;

  /*
    Nutrient Neutrality has no vintage and no meaningful issuance-date ordering, so it sorts on the
    nutrient. That keeps the grid's first column and the chart's bar order in agreement.

    The asset-manager surface prefixes its server keys with "results." where the project and public
    ones do not - a difference this resolver preserves rather than unifies, since the surfaces build
    their requests differently.

    "quantityType" is also the first column of the shared NN grid and is sortable there, which is
    what the public surface needs: its previous default named a "vintage" column that the NN column
    set does not contain, so the grid opened sorted by a column that was not on screen.
  */
  /*
    Restoration (KAN-9934) sorts the same way, for the same reason: it has no vintage, and the unit
    type leads its grid on every surface.
  */
  if (
    unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY ||
    unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_RESTORATION
  ) {
    if (surface === "project") return { gridColumnKey: "quantityType", serverSortKey: "quantityType" };
    if (surface === "public") return { gridColumnKey: "quantityType", serverSortKey: "quantityType" };
    if (surface === "assetManagerPreAccess")
      return { gridColumnKey: "quantityType", serverSortKey: "results.quantityType" };
  }

  switch (surface) {
    case "project":
      return {
        gridColumnKey: "",
        serverSortKey: isBiodiversity ? "issuanceDate" : "vintageStartDate",
      };
    case "public":
      return {
        gridColumnKey: isBiodiversity ? "issuanceDate" : "vintage",
        serverSortKey: isBiodiversity ? "issuanceDate" : "vintageStartDate",
      };
    case "assetManagerPreAccess":
    default:
      return {
        gridColumnKey: isBiodiversity ? "issuanceDate" : "vintage",
        serverSortKey: isBiodiversity ? "results.issuanceDate" : "results.vintageStartDate",
      };
  }
};

/**
 * Moves Carbon to the front of a measurement dropdown so it sits directly beneath "Overall".
 * Replaces two hand-rolled copies of the same idiom; three more remain outside this ticket's scope,
 * in shared/landholding/useLandholding, developer/dashboard and development-manager/dashboard.
 */
export const moveCarbonToFront = (selectData: SelectData, fromIndex = 1): SelectData => {
  const carbonIndex = selectData.findIndex((item) => item.key === ISSUANCE_UNIT_MEASUREMENT_CARBON);

  if (carbonIndex > fromIndex) {
    const reordered = [...selectData];
    const [carbonItem] = reordered.splice(carbonIndex, 1);
    reordered.splice(fromIndex, 0, carbonItem);
    return reordered;
  }

  return selectData;
};

export interface InventoryKeyColours {
  light: string;
  mid: string;
  dark: string;
  lightHover: string;
  midHover: string;
  darkHover: string;
}

/**
 * The colour family the dashboard and landholding surfaces swap by measurement: Carbon takes INDIGO,
 * Restoration (Restore 30x30) takes MAGENTA so it never reads as carbon, BNG or Nutrient Neutrality,
 * and every other measurement - Ecosystem recovery included - takes GREEN. Replaces the inline
 * ternaries that were repeated ten times in the landholding chart and three times in the old key.
 *
 * Every surface that charts Restoration units reads its palette from here, so the charts and keys
 * cannot drift. MAGENTA follows GREEN's shade steps rather than INDIGO's.
 *
 * Shared by the chart key widget and the landholding chart builder, so it lives here rather than in
 * the widget - the data builder must not have to import through the widget layer to colour a chart.
 */
export const getInventoryKeyColours = (unitMeasurement: string): InventoryKeyColours => {
  if (unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_CARBON) {
    return {
      light: ColorConstants.INDIGO_100,
      mid: ColorConstants.INDIGO_300,
      dark: ColorConstants.INDIGO_500,
      lightHover: ColorConstants.INDIGO_50,
      midHover: ColorConstants.INDIGO_200,
      darkHover: ColorConstants.INDIGO_400,
    };
  }

  if (unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_RESTORATION) {
    return {
      light: ColorConstants.MAGENTA_200,
      mid: ColorConstants.MAGENTA_400,
      dark: ColorConstants.MAGENTA_600,
      lightHover: ColorConstants.MAGENTA_100,
      midHover: ColorConstants.MAGENTA_300,
      darkHover: ColorConstants.MAGENTA_500,
    };
  }

  return {
    light: ColorConstants.GREEN_200,
    mid: ColorConstants.GREEN_400,
    dark: ColorConstants.GREEN_600,
    lightHover: ColorConstants.GREEN_100,
    midHover: ColorConstants.GREEN_300,
    darkHover: ColorConstants.GREEN_500,
  };
};

export interface RestorationUnitTotals {
  total: number;
  available: number;
  /** Sold + Reserved, which the public Directory shows as one figure. */
  allocated: number;
}

/**
 * A Restoration (Restore 30x30) project's unit totals, summed from its issuance rows - the public
 * projectDetails result carries only a cached total, with no available or allocated figure. Shared by
 * the public overview and its listing preview, which both already fetch the issuances (KAN-9935).
 */
export const getRestorationUnitTotals = (
  issuances: { unitQuantityTotal: number; unitQuantityAvailable: number }[]
): RestorationUnitTotals => {
  const total = issuances.reduce((sum, issuance) => sum + issuance.unitQuantityTotal, 0);
  const available = issuances.reduce((sum, issuance) => sum + issuance.unitQuantityAvailable, 0);

  return { total, available, allocated: total - available };
};

/**
 * The verified-series colours on the project, public and asset-manager surfaces - the charts and
 * their keys alike. Those surfaces keep GREEN for every measurement regardless of the dashboard's
 * swap, except Restoration, which takes its own family from getInventoryKeyColours so its bars
 * never read as BNG and always match the key.
 */
export const getInventoryVerifiedColours = (unitMeasurement: string): InventoryKeyColours =>
  unitMeasurement === ISSUANCE_UNIT_MEASUREMENT_RESTORATION
    ? getInventoryKeyColours(unitMeasurement)
    : {
        light: ColorConstants.GREEN_200,
        mid: ColorConstants.GREEN_400,
        dark: ColorConstants.GREEN_600,
        lightHover: ColorConstants.GREEN_100,
        midHover: ColorConstants.GREEN_300,
        darkHover: ColorConstants.GREEN_500,
      };

/**
 * The Nutrient Neutrality chart colours one bar family per nutrient rather than one per
 * verified/pending block, because NN has no pending concept - the only thing left to distinguish is
 * which nutrient a bar belongs to. Nitrogen takes AQUA and Phosphorus AMBER_ORANGE, per the Figma
 * design (Projects | Seed - Hub, node 5653-2076).
 *
 * Nutrients come off the server as free-text `quantityType` values, so an unrecognised one cannot
 * throw: it falls back to the next palette in NUTRIENT_FALLBACK_PALETTES by its position in the
 * chart, which keeps two unknown nutrients visually distinct instead of collapsing them onto one
 * colour.
 */
export const NUTRIENT_NITROGEN = "Nitrogen";

export const NUTRIENT_PHOSPHORUS = "Phosphorus";

const AQUA_PALETTE: InventoryKeyColours = {
  light: ColorConstants.AQUA_100,
  mid: ColorConstants.AQUA_300,
  dark: ColorConstants.AQUA_500,
  lightHover: ColorConstants.AQUA_50,
  midHover: ColorConstants.AQUA_200,
  darkHover: ColorConstants.AQUA_400,
};

const AMBER_ORANGE_PALETTE: InventoryKeyColours = {
  light: ColorConstants.AMBER_ORANGE_100,
  mid: ColorConstants.AMBER_ORANGE_300,
  dark: ColorConstants.AMBER_ORANGE_500,
  lightHover: ColorConstants.AMBER_ORANGE_50,
  midHover: ColorConstants.AMBER_ORANGE_200,
  darkHover: ColorConstants.AMBER_ORANGE_400,
};

const MINT_GREEN_PALETTE: InventoryKeyColours = {
  light: ColorConstants.MINT_GREEN_100,
  mid: ColorConstants.MINT_GREEN_300,
  dark: ColorConstants.MINT_GREEN_500,
  lightHover: ColorConstants.MINT_GREEN_50,
  midHover: ColorConstants.MINT_GREEN_200,
  darkHover: ColorConstants.MINT_GREEN_400,
};

const INDIGO_PALETTE: InventoryKeyColours = {
  light: ColorConstants.INDIGO_100,
  mid: ColorConstants.INDIGO_300,
  dark: ColorConstants.INDIGO_500,
  lightHover: ColorConstants.INDIGO_50,
  midHover: ColorConstants.INDIGO_200,
  darkHover: ColorConstants.INDIGO_400,
};

/** The two nutrients the design names. Anything else falls through to the list below. */
const NUTRIENT_PALETTE_BY_NAME: { [nutrient: string]: InventoryKeyColours } = {
  [NUTRIENT_NITROGEN]: AQUA_PALETTE,
  [NUTRIENT_PHOSPHORUS]: AMBER_ORANGE_PALETTE,
};

const NUTRIENT_FALLBACK_PALETTES: InventoryKeyColours[] = [
  AQUA_PALETTE,
  AMBER_ORANGE_PALETTE,
  MINT_GREEN_PALETTE,
  INDIGO_PALETTE,
];

/** The Available/Reserved/Sold colour triple a nutrient's bars and key block use. */
export const getNutrientChartColours = (nutrient: string, index: number): InventoryKeyColours =>
  NUTRIENT_PALETTE_BY_NAME[nutrient] ??
  NUTRIENT_FALLBACK_PALETTES[
    ((index % NUTRIENT_FALLBACK_PALETTES.length) + NUTRIENT_FALLBACK_PALETTES.length) %
      NUTRIENT_FALLBACK_PALETTES.length
  ];

/**
 * The two surfaces that offer an InventorySelector. The other three inventory surfaces (project,
 * public, assetManagerPreAccess) render a single grid with no view switcher, which is why this is a
 * narrower vocabulary than InventorySurface rather than a widening of it.
 */
export type InventoryViewButtonSurface = "landholding" | "dashboard";

/**
 * The view buttons a surface offers for a measurement.
 *
 * Extracted from two near-identical switches - the landholding inventory and the inventory
 * management board - that agreed on every measurement and disagreed only on the default. Keeping
 * them apart meant the five Nutrient Neutrality labels had to be written twice, in two files, with
 * nothing tying them together.
 *
 * The defaults are the whole reason `surface` is a parameter:
 *   - the dashboard treats "" (Overall) and any unrecognised measurement as its three Overall views;
 *   - the landholding offers no views at all in the same position.
 * Both are deliberate and preserved exactly as they shipped.
 *
 * Nutrient Neutrality omits "By NCA": the NN issuance schema does not declare `nca` (KAN-9738), so
 * the view would group every issuance into a single empty bucket.
 */
export const getInventoryViewButtons = (surface: InventoryViewButtonSurface, unitMeasurement: string): string[] => {
  switch (unitMeasurement) {
    case ISSUANCE_UNIT_MEASUREMENT_CARBON:
      return ["By project", "By vintage", "By type", "By unit"];
    case ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY:
      return ["By project", "By habitat group", "By condition", "By LPA", "By NCA", "By LNRS"];
    case ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY:
      return ["By project", "By nutrient", "By designated site", "By LPA", "By LNRS"];
    /*
      Restoration (KAN-9934): one issuance per project, so the project is the only meaningful
      dimension - no vintage, habitat or unit views, and no revenue view. The dashboard names it as its
      Overall view does, grouping by projectNameMeasurement; the landholding has no such view, so it
      takes "By project".
    */
    case ISSUANCE_UNIT_MEASUREMENT_RESTORATION:
      return surface === "dashboard" ? ["Units per project"] : ["By project"];
    default:
      return surface === "dashboard" ? ["Units per project", "Revenue per project", "Sales over time"] : [];
  }
};

/** One line of the dashboard's "Sales over time" chart, and the colour its line and key swatch take. */
export interface SalesOverTimeSeries {
  unitMeasurement: string;
  colour: string;
}

/**
 * The measurements the "Sales over time" chart draws, in legend order. The chart, its colours and its
 * key all read this one list, so a measurement cannot be drawn without a swatch or vice versa.
 *
 * Nutrient Neutrality takes SKY_BLUE, the dot its overview tile uses, and Restoration (Restore 30x30)
 * takes MAGENTA, its inventory family. Both chart a £ value here, so NN's kg/yr unit does not apply.
 */
export const SALES_OVER_TIME_SERIES: readonly SalesOverTimeSeries[] = [
  { unitMeasurement: ISSUANCE_UNIT_MEASUREMENT_CARBON, colour: ColorConstants.INDIGO_500 },
  { unitMeasurement: ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY, colour: ColorConstants.GREEN_500 },
  { unitMeasurement: ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY, colour: ColorConstants.SKY_BLUE_500 },
  { unitMeasurement: ISSUANCE_UNIT_MEASUREMENT_RESTORATION, colour: ColorConstants.MAGENTA_500 },
];

/**
 * The series an organisation's chart draws: only the measurements its measurement filter lists, since a
 * measurement missing there means the organisation holds no projects of that kind. Kept in legend order.
 */
export const getSalesOverTimeSeries = (measurementTypes: readonly string[]): SalesOverTimeSeries[] =>
  SALES_OVER_TIME_SERIES.filter((series) => measurementTypes.includes(series.unitMeasurement));

export interface SalesOverTimeTransaction {
  transactionDate: Date;
  unitMeasurement: string;
  adjustedValue?: number | null;
}

/**
 * The cumulative sold value per measurement, one point per transaction date.
 *
 * Walks the rows oldest-first regardless of how the table beneath is sorted - the running totals are
 * only meaningful in date order. Buckets are keyed on `YYYY-MM-DD` so they sort chronologically as
 * plain strings - a `DD/MM/YYYY` key is parsed as `MM/DD/YYYY` by `new Date`, so any day past the 12th
 * is `NaN` and the rest sort by the wrong month. The axis then reads as `DD/MM/YYYY`.
 *
 * A measurement outside SALES_OVER_TIME_SERIES is not drawn.
 */
export const buildSalesOverTimeChartData = (
  transactions: readonly SalesOverTimeTransaction[]
): { chartLabels: string[]; chartDatasets: ChartDatasets } => {
  const cumulative = new Map<string, number>(SALES_OVER_TIME_SERIES.map((s) => [s.unitMeasurement, 0]));
  const byDate: Record<string, Map<string, number>> = {};

  [...transactions]
    .sort((a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime())
    .forEach((item) => {
      const dateStr = convertDateToStringYYYYMMDD(item.transactionDate) ?? "";
      const total = cumulative.get(item.unitMeasurement);

      if (total !== undefined) cumulative.set(item.unitMeasurement, total + (item.adjustedValue ?? 0));

      // Snapshot every series, so a date only one measurement sold on still carries the others' totals.
      byDate[dateStr] = new Map(cumulative);
    });

  const chartDateKeys = Object.keys(byDate).sort();

  return {
    chartLabels: chartDateKeys.map((date) => {
      const parsed = parseDateOnlyStringYYYYMMDD(date);
      return parsed ? convertDateToStringDDMMYYYY(parsed) : date;
    }),
    chartDatasets: SALES_OVER_TIME_SERIES.map(({ unitMeasurement }) => ({
      label: unitMeasurement,
      data: chartDateKeys.map((date) => byDate[date].get(unitMeasurement) ?? 0),
    })),
  };
};

/** One stacked series of a nutrient chart, and the shade it takes from its nutrient's family. */
export interface NutrientChartSeries {
  /** The series name, prefixed with the nutrient in the tooltip. */
  label: string;
  /** The ResultData field this series reads. */
  key: string;
  shade: keyof InventoryKeyColours;
  hover: keyof InventoryKeyColours;
}

export interface NutrientStackedChartData {
  chartLabels: (string | string[])[];
  chartData: StackedChartData[];
  /** The nutrients drawn, in stack order, so a chart key can mirror them. */
  chartKeyNutrients: string[];
}

/**
 * The Nutrient Neutrality chart: one bar per DIMENSION value, coloured per nutrient.
 *
 * Four of the five NN views group by (dimension, nutrient), so the server returns one row per PAIR -
 * Winchester/Nitrogen and Winchester/Phosphorus are two rows. Drawn a row at a time, that is two
 * separate bars both labelled "Winchester". Collapse them: rows sharing a bar identity become one
 * bar, and each nutrient contributes its own segments to that bar's stack.
 *
 * Shared by the inventory management board and the landholding inventory. They differ only in their
 * series - the board always stacks Available/Reserved/Sold, the landholding swaps to
 * Allocated/Available for a viewer without the figures - which is why `series` is a parameter rather
 * than a constant here.
 *
 * `getBarIdentity` exists for the "By project" views: project display names carry no unique
 * constraint, so two projects sharing a name must stay two bars. Those views pass the project uuid;
 * every other view omits it and collapses on the label, which IS the dimension value.
 *
 * "By nutrient" falls out of the same algorithm without a special case: its dimension IS the
 * nutrient, so each bar holds exactly one and is therefore drawn in that nutrient's family.
 */
export const buildNutrientStackedChartData = (
  rows: ResultData[],
  labels: (string | string[])[],
  series: readonly NutrientChartSeries[],
  getNutrient: (row: ResultData) => string,
  getBarIdentity?: (row: ResultData) => string
): NutrientStackedChartData => {
  const chartLabels: (string | string[])[] = [];
  const barIndexByKey = new Map<string, number>();
  const nutrients: string[] = [];
  const totals = new Map<string, Record<string, number>[]>();

  rows.forEach((row, rowIndex) => {
    const label = labels[rowIndex] ?? "";
    // A two-line habitat label is an array, so key the bar on its serialised form, not on identity.
    const labelKey = JSON.stringify(label);
    const barKey = getBarIdentity ? `${getBarIdentity(row)}|${labelKey}` : labelKey;

    let barIndex = barIndexByKey.get(barKey);
    if (barIndex === undefined) {
      barIndex = chartLabels.length;
      barIndexByKey.set(barKey, barIndex);
      chartLabels.push(label);
    }

    const nutrient = getNutrient(row);
    let nutrientTotals = totals.get(nutrient);
    if (!nutrientTotals) {
      nutrientTotals = [];
      nutrients.push(nutrient);
      totals.set(nutrient, nutrientTotals);
    }

    const bar = nutrientTotals[barIndex] ?? {};
    series.forEach((s) => {
      bar[s.key] = (bar[s.key] ?? 0) + Number(row[s.key] ?? 0);
    });
    nutrientTotals[barIndex] = bar;
  });

  const chartData = nutrients.flatMap((nutrient, nutrientIndex) => {
    const colours = getNutrientChartColours(nutrient, nutrientIndex);
    const nutrientTotals = totals.get(nutrient) ?? [];

    return series.map((s) => ({
      // The tooltip reads this, so it has to name both halves of the segment.
      label: nutrient ? `${nutrient} - ${s.label}` : s.label,
      // A nutrient absent from a bar leaves a hole in its sparse array, which reads as zero.
      data: chartLabels.map((_, barIndex) => nutrientTotals[barIndex]?.[s.key] ?? 0),
      backgroundColor: colours[s.shade],
      hoverBackgroundColor: colours[s.hover],
    }));
  });

  return { chartLabels, chartData, chartKeyNutrients: nutrients };
};

/**
 * Which `groupByFieldContent` slot a Nutrient Neutrality group-by key puts the nutrient in.
 *
 * The four composite keys pack the nutrient into a second group-by slot; which slot depends on how
 * many the dimension itself needs. "By project" takes two - the display name and the uuid its row
 * link needs - so its nutrient lands in slot 3, while the other three dimensions are single-valued
 * and leave the nutrient in slot 2. `quantityType` is not composite at all: the nutrient IS the
 * dimension, so it is already in slot 1.
 *
 * Keyed on the server's group-by key rather than on a view label, so the inventory management board
 * and the landholding inventory - which label the same views differently - can both read it.
 */
export const NUTRIENT_GROUP_BY_SLOT: Record<string, 1 | 2 | 3> = {
  quantityType: 1,
  projectNameQuantityType: 3,
  designatedSiteQuantityType: 2,
  lpaQuantityType: 2,
  lnrsQuantityType: 2,
};

/** The nutrient a row carries, or "" where the group-by key does not select one. */
export const getNutrientFromRow = (groupCondition: string, row: ResultData): string => {
  const slot = NUTRIENT_GROUP_BY_SLOT[groupCondition];
  return slot ? ((row[`groupByFieldContent${slot}`] as string) ?? "") : "";
};

/**
 * The aggregate inventory grid's group-by columns, shared by the inventory management board and the
 * landholding inventory.
 *
 * Both surfaces render the same three `groupByFieldContent*` slots and the same area column, so the
 * definitions live here once. Each surface still spreads its own overrides onto them - the name and
 * the formatter change per view, and the landholding widens slot 1 - which is why these are bases
 * rather than finished columns.
 */
export const INVENTORY_GROUP_BY_COLUMN_1: DataGridColumnDefinition = {
  name: "Name1",
  key: "groupByFieldContent1",
  dataType: "string",
  formatter: "stringWithEllipsis",
  filterable: true,
  sortable: true,
};

export const INVENTORY_GROUP_BY_COLUMN_2: DataGridColumnDefinition = {
  name: "Name2",
  key: "groupByFieldContent2",
  dataType: "string",
  formatter: "stringWithEllipsis",
  filterable: true,
  sortable: true,
};

export const INVENTORY_GROUP_BY_COLUMN_3: DataGridColumnDefinition = {
  name: "Name3",
  key: "groupByFieldContent3",
  dataType: "string",
  formatter: "stringWithEllipsis",
  filterable: true,
  sortable: true,
};

export const INVENTORY_AREA_HA_COLUMN: DataGridColumnDefinition = {
  name: "Unit size (Ha)",
  key: "areaHa",
  dataType: "number",
  filterable: true,
  sortable: true,
};

/**
 * The five unit columns every aggregate inventory view carries for a viewer who sees the full
 * figures. The board appends Avg. sale price and Transactions to these; the landholding inventory
 * uses them as they are, and swaps to its own Allocated/Available pair for a viewer without them.
 */
export const INVENTORY_UNIT_COLUMNS: DataGridColumnDefinition[] = [
  { name: "Available", key: "availableUnits", dataType: "number", filterable: true, sortable: true },
  { name: "Reserved", key: "reservedUnits", dataType: "number", filterable: true, sortable: true },
  // Totalled alongside the other unit columns on this dashboard (see `totalColumnNames`); the project
  // inventory tab deliberately leaves Quoted out of its totals per BR-02845.
  { name: "Quoted", key: "quotedUnits", dataType: "number", filterable: true, sortable: true },
  { name: "Sold", key: "soldUnits", dataType: "number", filterable: true, sortable: true },
  {
    name: "% sold",
    key: "percentageSold",
    formatter: "percentage",
    dataType: "number",
    filterable: true,
    sortable: true,
  },
];

/**
 * The aggregate grid columns whose value is a quantity of units, and which therefore carry a
 * measurement's unit. `% sold`, `Avg. sale price`, `Total value` and the area columns are excluded:
 * they are a proportion, a price and an area, none of them a count of units.
 */
const INVENTORY_QUANTITY_COLUMN_KEYS = [
  "allocatedUnits",
  "availableUnits",
  "quotedUnits",
  "reservedUnits",
  "soldUnits",
];

/**
 * Suffixes a column set's quantity headers with the measurement's unit, e.g. "Available (kg/yr)".
 *
 * Returns the columns untouched for every measurement without one, which is all of them but Nutrient
 * Neutrality - so a surface can wrap its whole column set and leave Carbon and Biodiversity
 * byte-identical.
 *
 * Applied by the caller rather than baked into INVENTORY_UNIT_COLUMNS because the two consuming
 * surfaces do not agree yet: the landholding inventory labels its headers per D-4, and whether the
 * inventory management board follows is KAN-9744's to decide.
 */
export const withInventoryUnitLabel = (
  columns: DataGridColumnDefinition[],
  unitMeasurement: string
): DataGridColumnDefinition[] => {
  const unitLabel = getInventoryUnitLabel(unitMeasurement);

  if (!unitLabel) return columns;

  return columns.map((column) =>
    INVENTORY_QUANTITY_COLUMN_KEYS.includes(column.key) ? { ...column, name: `${column.name} (${unitLabel})` } : column
  );
};

/** The group-by column bases a surface supplies to getNutrientDimensionColumns. */
export interface InventoryGroupByColumns {
  name1: DataGridColumnDefinition;
  name2: DataGridColumnDefinition;
  name3: DataGridColumnDefinition;
}

/**
 * The DIMENSION columns a Nutrient Neutrality view renders, for either aggregate surface.
 *
 * Keyed on the server's group-by key rather than on a view label, for the same reason
 * NUTRIENT_GROUP_BY_SLOT is: the board and the landholding inventory label the same five views
 * differently, and the slot a nutrient arrives in is a property of the key, not of the label. It is
 * in fact the same fact twice over - a view that packs the nutrient into slot 2 renders it from slot
 * 2 - so keying both on the key keeps them from drifting.
 *
 * The caller passes its own column bases: the two surfaces size slot 1 differently, and neither
 * width is this function's to decide. Returns an empty array for any other key, which is what both
 * dictionaries did with a missing entry.
 *
 * "By nutrient" (`quantityType`) is the one single-column view - its dimension value IS the
 * nutrient, so a second column beside it would repeat itself. The other four carry the nutrient
 * beside their dimension, which is why they request a composite group-by key.
 */
export const getNutrientDimensionColumns = (
  groupByKey: string,
  { name1, name2, name3 }: InventoryGroupByColumns
): DataGridColumnDefinition[] => {
  const { ISSUANCE_FIELD_LABELS } = ProjectIssuanceConstants;

  switch (groupByKey) {
    case "projectNameQuantityType":
      return [
        { ...name1, key: "link", name: "Project", formatter: "link" },
        // Slot 2 holds the project uuid the link needs, so the nutrient arrives in slot 3.
        { ...name3, name: ISSUANCE_FIELD_LABELS.quantityType },
      ];
    case "quantityType":
      return [{ ...name1, name: ISSUANCE_FIELD_LABELS.quantityType }];
    case "designatedSiteQuantityType":
      return [
        { ...name1, name: ISSUANCE_FIELD_LABELS.designatedSite },
        { ...name2, name: ISSUANCE_FIELD_LABELS.quantityType },
      ];
    case "lpaQuantityType":
      return [
        { ...name1, name: ISSUANCE_FIELD_LABELS.lpa },
        { ...name2, name: ISSUANCE_FIELD_LABELS.quantityType },
      ];
    case "lnrsQuantityType":
      return [
        { ...name1, name: ISSUANCE_FIELD_LABELS.lnrs },
        { ...name2, name: ISSUANCE_FIELD_LABELS.quantityType },
      ];
    default:
      return [];
  }
};
